import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { Check, ChevronDown, ArrowRight } from "lucide-react";
import { FadeUp, AccordionContent, CardHover, StaggerTableBody, tableRowVariant } from "../hooks/motion";
import { motion } from "framer-motion";
import { CALCOM_DEMO_URL } from "../config/demo";
import { getAuthSignUpURL, SIGNUP_EVENT } from "../config/auth";

const AccordionItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1E1E2E]">
      <button
        onClick={() => setOpen(!open)}
        data-testid="pricing-faq-item"
        className="w-full text-left py-5 flex items-center justify-between gap-4"
      >
        <span className="text-[#F4F4F8] font-medium">{question}</span>
        <ChevronDown className={`text-[#8B8BA7] w-5 h-5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AccordionContent open={open}>
        <p className="text-[#8B8BA7] leading-relaxed">{answer}</p>
      </AccordionContent>
    </div>
  );
};

const PLANS = [
  {
    name: "Starter",
    monthly: 79,
    annual: 65,
    tagline: "For solo operators just getting started.",
    features: [
      "1 user seat",
      "Up to 100 quotes/month",
      "SMS quoting",
      "Basic follow-up sequences",
      "Review automation",
      "Unified inbox",
      "Email support",
    ],
    popular: false,
    cta: "Book a Demo",
    testid: "pricing-starter",
  },
  {
    name: "Growth",
    monthly: 179,
    annual: 149,
    tagline: "The most popular plan for growing teams.",
    features: [
      "Up to 3 user seats",
      "Unlimited quotes",
      "Advanced follow-up automation",
      "Custom sequence builder",
      "Full pipeline view",
      "Team inbox & assignment",
      "Priority support",
      "All Starter features",
    ],
    popular: true,
    cta: "Book a Demo",
    testid: "pricing-growth",
  },
  {
    name: "Pro",
    monthly: 349,
    annual: 299,
    tagline: "For multi-location teams and growing franchises.",
    features: [
      "Unlimited user seats",
      "Unlimited everything",
      "Custom integrations",
      "API access",
      "Multi-location support",
      "Dedicated account manager",
      "Onboarding & training",
      "All Growth features",
    ],
    popular: false,
    cta: "Book a Demo",
    testid: "pricing-pro",
  },
];

const COMPARE_ROWS = [
  { label: "SMS quoting", starter: true, growth: true, pro: true },
  { label: "Quotes per month", starter: "100", growth: "Unlimited", pro: "Unlimited" },
  { label: "User seats", starter: "1", growth: "3", pro: "Unlimited" },
  { label: "Basic follow-ups", starter: true, growth: true, pro: true },
  { label: "Advanced automation", starter: false, growth: true, pro: true },
  { label: "Custom sequence builder", starter: false, growth: true, pro: true },
  { label: "Review automation", starter: true, growth: true, pro: true },
  { label: "Pipeline view", starter: false, growth: true, pro: true },
  { label: "Multi-location", starter: false, growth: false, pro: true },
  { label: "API access", starter: false, growth: false, pro: true },
  { label: "Support", starter: "Email", growth: "Priority", pro: "Dedicated" },
];

const FAQ_ITEMS = [
  { q: "Is there a free trial?", a: "No. We don't offer free trials because we've found they don't serve owner-operators well. Instead, book a demo — we'll show you exactly how CleanBid works for your specific business, and you can start with full access from day one." },
  { q: "Are there any long-term contracts?", a: "No. CleanBid is month-to-month on all plans. Cancel anytime, no questions asked." },
  { q: "Can I change plans later?", a: "Yes. Upgrade or downgrade at any time. Changes take effect on your next billing cycle." },
  { q: "What's included in the annual discount?", a: "Annual plans save you 20% compared to monthly billing. The discount is applied immediately and locked in for 12 months." },
  { q: "What payment methods do you accept?", a: "All major credit and debit cards through Stripe. You'll be billed automatically each month or year depending on your plan." },
];

const CellVal = ({ val }) => {
  if (val === true) return <Check className="w-4 h-4 text-[#71DED6] mx-auto" />;
  if (val === false) return <span className="text-[#8B8BA7]/40 text-lg mx-auto block text-center">—</span>;
  return <span className="text-sm text-[#F4F4F8] block text-center">{val}</span>;
};

export default function PricingPage() {
  usePageTitle("Pricing | CleanBid.ai");
  const [annual, setAnnual] = useState(false);

  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <section className="py-24 text-center">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Pricing</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-6">
              Pay for results, <br />
              <span className="text-gradient-green">not features.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={120}>
            <p className="text-lg text-[#8B8BA7] mb-8">
              Every CleanBid plan includes SMS quoting, follow-up automation, and review collection. Pick the size that fits your team.
            </p>
          </FadeUp>

          {/* Toggle */}
          <FadeUp delay={160}>
            <div className="inline-flex items-center gap-4 bg-[#111118] border border-[#1E1E2E] rounded-full p-1.5" data-testid="billing-toggle">
              <button
                onClick={() => setAnnual(false)}
                data-testid="billing-monthly"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${!annual ? "bg-[#1E1E2E] text-[#F4F4F8]" : "text-[#8B8BA7] hover:text-[#F4F4F8]"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                data-testid="billing-annual"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${annual ? "bg-[#1E1E2E] text-[#F4F4F8]" : "text-[#8B8BA7] hover:text-[#F4F4F8]"}`}
              >
                Annual
                <span className="bg-[#71DED6] text-[#10151D] text-[10px] font-bold px-2 py-0.5 rounded-full">-20%</span>
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Plans */}
      <section className="pb-24" data-testid="pricing-plans-section">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan, i) => (
              <FadeUp key={i} delay={i * 120}>
                <CardHover className="h-full">
                <div
                  data-testid={plan.testid}
                  className={`bg-[#111118] rounded-3xl p-8 flex flex-col relative overflow-hidden h-full transition-[border-color,box-shadow] duration-300 ${
                    plan.popular
                      ? "border-2 border-[#71DED6]/50 shadow-glow-green"
                      : "border border-[#1E1E2E]"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute top-6 right-6 bg-[#71DED6] text-[#10151D] text-xs font-bold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#8B8BA7] uppercase tracking-widest">{plan.name}</p>
                    <div className="flex items-end gap-1 mt-4 mb-1">
                      <span className="text-5xl font-bold text-[#F4F4F8] tracking-tight">
                        ${annual ? plan.annual : plan.monthly}
                      </span>
                      <span className="text-[#8B8BA7] mb-2">/mo</span>
                    </div>
                    {annual && (
                      <p className="text-xs text-[#71DED6] mb-1">Billed annually</p>
                    )}
                    <p className="text-[#8B8BA7] text-sm mb-6">{plan.tagline}</p>
                    <ul className="space-y-3">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2.5 text-sm text-[#F4F4F8]">
                          <Check className="w-4 h-4 text-[#71DED6] flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                    className={`mt-8 block text-center py-3.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                      plan.popular
                        ? "bg-[#71DED6] text-[#10151D] hover:scale-[1.02] hover:shadow-glow-green-sm"
                        : "border border-[#1E1E2E] text-[#F4F4F8] hover:bg-[#1E1E2E]"
                    }`}
                  >
                    {plan.cta}
                  </a>
                  <a
                    href={getAuthSignUpURL()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`plan-signup-${plan.name.toLowerCase()}`}
                    data-event={SIGNUP_EVENT}
                    className="block text-center text-xs text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors mt-3"
                  >
                    or Sign Up directly →
                  </a>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-24 border-t border-[#1E1E2E]" data-testid="feature-comparison-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <FadeUp className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8]">
              Compare plans
            </h2>
          </FadeUp>
          <FadeUp>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="plan-comparison-table">
                <thead>
                  <tr>
                    <th className="text-left py-4 px-4 text-[#8B8BA7] text-sm font-medium">Feature</th>
                    <th className="py-4 px-4 text-center text-[#8B8BA7] text-sm font-medium">Starter</th>
                    <th className="py-4 px-4 text-center">
                      <div className="bg-[#71DED6]/10 border border-[#71DED6]/20 rounded-lg py-1 px-2 inline-block">
                        <span className="text-[#71DED6] font-bold text-sm">Growth</span>
                      </div>
                    </th>
                    <th className="py-4 px-4 text-center text-[#8B8BA7] text-sm font-medium">Pro</th>
                  </tr>
                </thead>
                <StaggerTableBody>
                  {COMPARE_ROWS.map((row, i) => (
                    <motion.tr key={i} variants={tableRowVariant} className="border-t border-[#1E1E2E]">
                      <td className="py-3.5 px-4 text-[#8B8BA7] text-sm">{row.label}</td>
                      <td className="py-3.5 px-4"><CellVal val={row.starter} /></td>
                      <td className="py-3.5 px-4 bg-[#71DED6]/5"><CellVal val={row.growth} /></td>
                      <td className="py-3.5 px-4"><CellVal val={row.pro} /></td>
                    </motion.tr>
                  ))}
                </StaggerTableBody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-24 bg-[#111118]/40" data-testid="roi-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-6">
              What's one closed job worth to you?
            </h2>
          </FadeUp>
          <FadeUp delay={60}>
            <p className="text-lg text-[#8B8BA7] max-w-2xl mx-auto leading-relaxed mb-8">
              The average home service job is worth $250–$400. CleanBid users close an average of 6 extra jobs per month from leads they would have lost without follow-up. At $79/month, the math is simple.
            </p>
          </FadeUp>
          <FadeUp delay={120}>
            <div className="grid grid-cols-3 gap-6">
              {[
                { val: "6", label: "extra jobs/month avg", color: "green" },
                { val: "$1,500+", label: "additional revenue", color: "green" },
                { val: "19x", label: "typical ROI", color: "indigo" },
              ].map((stat, i) => (
                <div key={i} className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6">
                  <p className={`text-3xl font-bold ${stat.color === "green" ? "text-[#71DED6]" : "text-[#6B5CF6]"}`}>
                    {stat.val}
                  </p>
                  <p className="text-[#8B8BA7] text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" data-testid="pricing-faq-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <FadeUp className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8]">
              Pricing questions
            </h2>
          </FadeUp>
          <FadeUp>
            <div className="divide-y divide-[#1E1E2E]">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 glow-cta" data-testid="pricing-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              Ready to start closing more jobs?
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] mb-8">Book a 20-minute demo. See it live. Start that week.</p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-page-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
